"use client";

import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";

interface SidebarLinkProps {
  link: {
    label: string;
    href: string;
    icon?: React.ReactNode;
  };
  open?: boolean;
}

export function SidebarLink({ link, open = true }: SidebarLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === link.href || 
    (link.href !== "/" && location.pathname.startsWith(link.href));

  return (
    <Link
      to={link.href}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 group",
        isActive
          ? "bg-primary text-[#1A1C1E] shadow-sm"
          : "text-neutral-400 hover:text-white hover:bg-white/5",
        !open && "justify-center px-2"
      )}
    >
      {link.icon && (
        <span className={cn(
          "shrink-0 transition-colors",
          isActive ? "[&>svg]:text-[#1A1C1E]" : "group-hover:[&>svg]:text-white",
          !open && "mx-auto"
        )}>
          {link.icon}
        </span>
      )}
      {open && (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="whitespace-nowrap"
        >
          {link.label}
        </motion.span>
      )}
    </Link>
  );
}

interface SidebarBodyProps {
  children: React.ReactNode;
  className?: string;
}

export function SidebarBody({ children, className }: SidebarBodyProps) {
  return (
    <div className={cn("flex flex-col h-full", className)}>
      {children}
    </div>
  );
}

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  children: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function Sidebar({ open, setOpen, children, onMouseEnter, onMouseLeave }: SidebarProps) {
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        "bg-[#1A1C1E] text-white border-r border-white/5 flex-shrink-0 flex flex-col transition-all duration-300 ease-in-out h-screen",
        open ? "w-64" : "w-16"
      )}
    >
      <div className="p-4 h-full flex flex-col">
        {children}
      </div>
    </div>
  );
}

// SidebarMenu components for collapsible sections
export function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      className={cn("flex w-full min-w-0 flex-col gap-1", className)}
      {...props}
    />
  );
}

export function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      className={cn("group/menu-item relative", className)}
      {...props}
    />
  );
}

interface SidebarMenuButtonProps extends React.ComponentProps<"button"> {
  isActive?: boolean;
  size?: "default" | "lg";
}

export const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ 
    className, 
    isActive = false,
    size = "default",
    children,
    ...props 
  }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          "flex w-full items-center gap-2 overflow-hidden rounded-md text-left outline-none transition-all",
          size === "lg" ? "px-3 py-2" : "px-3 py-2",
          isActive
            ? "bg-primary text-[#1A1C1E] font-medium"
            : "text-neutral-400 hover:text-white hover:bg-white/5",
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";

export function SidebarMenuSub({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      className={cn(
        "ml-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-white/10 pl-2.5 py-0.5",
        className
      )}
      {...props}
    />
  );
}

export function SidebarMenuSubItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      className={cn("group/menu-sub-item relative", className)}
      {...props}
    />
  );
}

interface SidebarMenuSubButtonProps {
  isActive?: boolean;
  href?: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

export function SidebarMenuSubButton({ 
  className, 
  isActive = false,
  children,
  href,
  onClick,
  ...props 
}: SidebarMenuSubButtonProps) {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick();
    }
    if (href) {
      e.preventDefault();
      navigate(href);
    }
  };
  
  if (href) {
    return (
      <Link
        to={href}
        onClick={handleClick}
        className={cn(
          "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sm outline-none transition-all",
          isActive
            ? "bg-primary/20 text-white font-medium"
            : "text-neutral-400 hover:text-white hover:bg-white/5",
          className
        )}
        {...props}
      >
        {children}
      </Link>
    );
  }
  
  return (
    <button
      onClick={handleClick}
      className={cn(
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sm outline-none transition-all",
        isActive
          ? "bg-primary/20 text-white font-medium"
          : "text-neutral-400 hover:text-white hover:bg-white/5",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

